import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Shirt, Loader2, Ruler, Sparkles, Heart, Eye, Flame, User, UserPlus, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { API } from "@/lib/api";
import axios from "axios";

export default function Showcase() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const token = typeof window !== "undefined" ? localStorage.getItem("atelier_token") : null;

  useEffect(() => {
    axios
      .get(`${API}/public/showcase/${handle}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then((r) => {
        setData(r.data);
        setFollowing(!!r.data.is_following);
        setFollowers(r.data.followers || 0);
      })
      .catch(() => setData(null));
  }, [handle, token]);

  const toggleFollow = async () => {
    if (!token) {
      toast.info("Accedi per seguire questa vetrina");
      navigate("/auth");
      return;
    }
    setFollowBusy(true);
    try {
      const cfg = { headers: { Authorization: `Bearer ${token}` } };
      if (following) {
        const r = await axios.delete(`${API}/showcase/${handle}/follow`, cfg);
        setFollowing(false);
        setFollowers(r.data.followers);
        toast.success("Non segui più questa vetrina");
      } else {
        const r = await axios.post(`${API}/showcase/${handle}/follow`, {}, cfg);
        setFollowing(true);
        setFollowers(r.data.followers);
        toast.success("Ora segui questa vetrina! Riceverai un avviso ai nuovi look");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setFollowBusy(false);
    }
  };

  if (data === undefined)
    return (
      <div className="min-h-[70vh] grid place-items-center">
        <Loader2 className="h-8 w-8 text-[#2f74f0] animate-spin" />
      </div>
    );

  if (data === null)
    return (
      <div className="min-h-[70vh] grid place-items-center px-4 text-center">
        <div>
          <Shirt className="h-12 w-12 text-[#a9772f]/50 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#2e2015]">Vetrina non trovata</h1>
          <p className="text-[#6f5c47] mt-2">Questa vetrina non esiste o non è più pubblica.</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white rounded-full px-6">
            Vai su My Look
          </Button>
        </div>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-[#a9772f]">Vetrina pubblica</span>
        <h1 data-testid="showcase-title" className="font-serif text-3xl sm:text-5xl font-semibold text-[#2e2015] mt-2">{data.title}</h1>
        <p className="text-[#6f5c47] mt-2">di <span className="font-medium text-[#3a2a1c]">{data.name}</span></p>
        {data.bio && <p data-testid="showcase-bio" className="text-[#6f5c47] mt-4">{data.bio}</p>}
        <div className="mt-5 flex items-center justify-center gap-4 flex-wrap">
          <span data-testid="showcase-followers-count" className="inline-flex items-center gap-1.5 text-sm text-[#6f5c47]">
            <UserCheck className="h-4 w-4 text-[#2f74f0]" /> {followers} follower
          </span>
          {!data.is_owner && (
            <Button
              data-testid="showcase-follow-btn"
              onClick={toggleFollow}
              disabled={followBusy}
              variant={following ? "outline" : "default"}
              className={following
                ? "rounded-full px-6 border-[#1e5fe0]/40 text-[#1a4fc0] hover:bg-[#1e5fe0]/10 bg-transparent"
                : "rounded-full px-6 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold"}
            >
              {followBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : following ? (
                <><UserCheck className="mr-2 h-4 w-4" /> Segui già</>
              ) : (
                <><UserPlus className="mr-2 h-4 w-4" /> Segui</>
              )}
            </Button>
          )}
        </div>
      </div>

      {data.style_card && (data.style_card.body_shape || (data.style_card.palette || []).length > 0) && (
        <div data-testid="showcase-style-card" className="mt-8 max-w-2xl mx-auto rounded-2xl border border-[#1e5fe0]/25 bg-white/80 p-5 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#a9772f]">
            <User className="h-4 w-4" /> Biglietto da visita stile
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-5">
            {data.style_card.body_shape && (
              <div>
                <div className="text-xs text-[#8a7760] uppercase tracking-wide">Corporatura</div>
                <div className="font-serif text-2xl text-[#2e2015] mt-0.5">{data.style_card.body_shape}</div>
                <div className="text-sm text-[#6f5c47] mt-1">
                  {[data.style_card.gender, data.style_card.height].filter(Boolean).join(" · ")}
                </div>
              </div>
            )}
            {(data.style_card.palette || []).length > 0 && (
              <div>
                <div className="text-xs text-[#8a7760] uppercase tracking-wide">Palette preferita</div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {data.style_card.palette.map((c, i) => (
                    <span key={i} data-testid="palette-swatch" className="inline-flex items-center gap-1.5 rounded-full border border-[#1e5fe0]/15 bg-white pl-1 pr-2.5 py-1">
                      <span className="h-5 w-5 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: c.hex }} />
                      <span className="text-xs text-[#4d3a2a] capitalize">{c.name}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {data.looks.length === 0 ? (
        <div className="mt-12 rounded-xl border border-dashed border-[#1e5fe0]/25 bg-white/70 p-16 text-center text-[#8a7760]">
          Ancora nessun look in questa vetrina.
        </div>
      ) : (
        <div data-testid="showcase-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {data.looks.map((o, i) => (
            <motion.div
              key={o.share_id}
              data-testid="showcase-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              onClick={() => navigate(`/share/${o.share_id}`)}
              className="group cursor-pointer rounded-xl border border-[#1e5fe0]/20 bg-white/75 overflow-hidden hover:border-[#2f74f0]/50 transition-colors"
            >
              <div className="relative h-72 bg-[#dce6ed] overflow-hidden">
                {o.has_image ? (
                  <img
                    src={`${API}/public/outfits/${o.share_id}/image`}
                    alt={o.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="h-full grid place-items-center"><Shirt className="h-10 w-10 text-[#a9772f]/40" /></div>
                )}
                {o.views >= 10 && (
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#a9772f] rounded-full px-2 py-0.5">
                    <Flame className="h-3 w-3" /> Popolare
                  </span>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-serif text-lg text-[#2e2015] truncate">{o.title}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="outline" className="border-[#1e5fe0]/30 text-[#1a4fc0] text-xs">{o.style}</Badge>
                  <span className="text-[#2f74f0] font-mono text-sm">€{o.total_price}</span>
                  {o.likes > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#8a7760]"><Heart className="h-3 w-3" />{o.likes}</span>
                  )}
                  {o.views > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#8a7760]"><Eye className="h-3 w-3" />{o.views}</span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <div className="mt-14 rounded-2xl border border-[#1e5fe0]/25 bg-gradient-to-br from-white/80 to-[#c4d6e4] p-8 text-center">
        <Ruler className="h-6 w-6 text-[#2f74f0] mx-auto mb-3" />
        <h2 className="font-serif text-2xl text-[#2e2015]">Crea la tua vetrina di look</h2>
        <p className="text-[#6f5c47] mt-2 max-w-xl mx-auto">Carica le tue foto e lascia che l'IA di My Look componga outfit completi sulla tua corporatura.</p>
        <Button
          data-testid="showcase-cta"
          onClick={() => navigate("/auth")}
          className="mt-6 bg-[#1e5fe0] hover:bg-[#2f74f0] text-white font-semibold rounded-full px-8 h-12"
        >
          <Sparkles className="mr-2 h-4 w-4" /> Inizia gratis
        </Button>
      </div>
    </div>
  );
}
